import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Parse from "parse";

const GameContext = createContext();

/* ================================================================
   상수
   ================================================================ */
const TIME_STORAGE_KEY = "GAME_TIME_REMAIN";
const ADMIN_TIME_KEY = "ADMIN_TIME_KEY";
const PLAYER_NAME_KEY = "PLAYER_NAME";
const PLAYER_KEY_CODE = "PLAYER_KEY_CODE";

const DEFAULT_GAME_TIME = 2400; // CourseList 조회 실패 시 폴백(초)
const MISSION_TOTAL = 33;

const COURSE_CLASS = "CourseList";
const PROG_PREFIX = "op"; // 입장 코드 접두어

const FINAL_QUIZ_CODE = [
  "end_q1",
  "end_q2",
  "end_q3",
  "end_q4",
  "end_q5",
  "end_q6",
  "end_q_finish",
  "finish",
];

/* ================================================================
   유틸
   ================================================================ */
const getRoundFromCode = (inCode, prefix) => {
  if (!inCode || !prefix) return null;
  const alpha = inCode.slice(prefix.length, prefix.length + 1);
  const round = alpha.charCodeAt(0) - 96;
  return round > 0 ? round : null;
};

const todayKST = () => {
  const kst = new Date(Date.now() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
};

export function GameProvider({ children }) {
  const location = useLocation();

  /* ── 팀명 ── */
  const [introTeamName, setIntroTeamName] = useState(
    () => localStorage.getItem("INTRO_TEAM") || "",
  );
  useEffect(() => {
    if (introTeamName) localStorage.setItem("INTRO_TEAM", introTeamName);
  }, [introTeamName]);

  /* ── 입장코드 ── */
  const [introInCodeV, setIntroInCodeV] = useState(
    () => localStorage.getItem("INTRO_CODE") || "",
  );
  useEffect(() => {
    if (introInCodeV) localStorage.setItem("INTRO_CODE", introInCodeV);
  }, [introInCodeV]);

  /* ── 이름 ── */
  const [playerName, setPlayerName] = useState(
    () => localStorage.getItem(PLAYER_NAME_KEY) || "",
  );
  useEffect(() => {
    if (playerName) localStorage.setItem(PLAYER_NAME_KEY, playerName);
  }, [playerName]);

  /* ── keyCode ── */
  const [playerKeyCode, setPlayerKeyCode] = useState(
    () => localStorage.getItem(PLAYER_KEY_CODE) || "",
  );
  useEffect(() => {
    if (playerKeyCode) localStorage.setItem(PLAYER_KEY_CODE, playerKeyCode);
  }, [playerKeyCode]);

  /* ── 게임시간 (관리자 설정 원래값) ── */
  const [adminGameTime, setAdminGameTime] = useState(() => {
    const saved = localStorage.getItem(ADMIN_TIME_KEY);
    return saved !== null ? Number(saved) : DEFAULT_GAME_TIME;
  });

  /* ── 타이머 ── */
  const [time, setTime] = useState(() => {
    const savedTime = localStorage.getItem(TIME_STORAGE_KEY);
    const savedAdmin = localStorage.getItem(ADMIN_TIME_KEY);
    if (savedTime !== null) return Number(savedTime);
    if (savedAdmin !== null) return Number(savedAdmin);
    return DEFAULT_GAME_TIME;
  });
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => {
      setTime((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          localStorage.removeItem(TIME_STORAGE_KEY);
          alert("시간이 초과되었습니다.");
          return 0;
        }
        const next = prev - 1;
        localStorage.setItem(TIME_STORAGE_KEY, next);
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  /* ── CourseList에서 게임시간 조회 ── */
  const fetchAdminTime = async (inCode) => {
    try {
      const code = inCode || localStorage.getItem("INTRO_CODE") || "";
      const round = getRoundFromCode(code, PROG_PREFIX);
      const today = todayKST();

      if (round !== null) {
        const q = new Parse.Query(COURSE_CLASS);
        q.equalTo("prefix", PROG_PREFIX);
        q.equalTo("round", round);
        q.equalTo("useDate", today);
        q.descending("createdAt");
        const course = await q.first();

        if (course) {
          const sec = course.get("duration") * 60;
          setAdminGameTime(sec);
          localStorage.setItem(ADMIN_TIME_KEY, String(sec));
          console.log(`✅ 관리자 시간: ${course.get("duration")}분 (${sec}초)`);
          return sec;
        }
      }
      console.warn("⚠ CourseList 없음 → 기본값");
      return DEFAULT_GAME_TIME;
    } catch (e) {
      console.warn("게임시간 조회 실패:", e.message);
      return DEFAULT_GAME_TIME;
    }
  };

  /* ── 이어하기 동기화 ── */
  useEffect(() => {
    if (!location.state?.isContinue) return;
    const savedTime = localStorage.getItem(TIME_STORAGE_KEY);
    const savedStages = localStorage.getItem("clearedStages");
    if (savedTime !== null) setTime(Number(savedTime));
    if (savedStages) setClearedStages(new Set(JSON.parse(savedStages)));
  }, [location.state]);

  /* ── 타이머 DB 저장 ── */
  useEffect(() => {
    const userId = localStorage.getItem("gameUserId");
    if (!userId) return;
    const query = new Parse.Query(Parse.Object.extend("silence1"));
    query
      .get(userId)
      .then((user) => {
        user.set("timeRemaining", time);
        return user.save();
      })
      .catch((err) => console.warn("타이머 DB 저장 실패:", err.message));
  }, [time]);

  /* ── 스테이지 ── */
  const [clearedStages, setClearedStages] = useState(() => {
    const saved = localStorage.getItem("clearedStages");
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  useEffect(() => {
    localStorage.setItem("clearedStages", JSON.stringify([...clearedStages]));
  }, [clearedStages]);

  useEffect(() => {
    const userId = localStorage.getItem("gameUserId");
    if (!userId) return;
    const query = new Parse.Query(Parse.Object.extend("silence1"));
    query
      .get(userId)
      .then((user) => {
        user.set("clearedStages", [...clearedStages]);
        return user.save();
      })
      .catch((err) => console.warn("스테이지 DB 저장 실패:", err.message));
  }, [clearedStages]);

  const clearStageOnce = (stageKey) => {
    if (!stageKey) return;
    setClearedStages((prev) => {
      if (prev.has(stageKey)) return prev;
      const next = new Set(prev);
      next.add(stageKey);
      return next;
    });
  };
  const increaseMissionOnce = (missionKey) => clearStageOnce(missionKey);

  /* ── 루트(/) 초기화 ── */
  useEffect(() => {
    if (location.pathname !== "/") return;
    setIsRunning(false);
    setTime(DEFAULT_GAME_TIME);
    setAdminGameTime(DEFAULT_GAME_TIME);
    localStorage.removeItem(TIME_STORAGE_KEY);
    localStorage.removeItem(ADMIN_TIME_KEY);
    localStorage.removeItem("clearedStages");
    localStorage.removeItem("gameUserId");
    localStorage.removeItem(PLAYER_NAME_KEY);
    localStorage.removeItem(PLAYER_KEY_CODE);
    localStorage.removeItem("INTRO_TEAM");
    localStorage.removeItem("INTRO_CODE");
    setPlayerName("");
    setPlayerKeyCode("");
    setIntroTeamName("");
    setIntroInCodeV("");
    setHintView("NONE");
    setHintIndex(null);
    setBackgroundNone(false);
    setClearedStages(new Set());
    setCorrectCount(0);
  }, [location.pathname]);

  /* ── 최종 추론 ── */
  const isFinalQuiz = (code) => FINAL_QUIZ_CODE.includes(code);
  const [correctCount, setCorrectCount] = useState(0);

  /* ── 닉네임 중복 확인 ── */
  const checkNicknameDuplicate = async (nickname) => {
    if (!nickname?.trim()) return { isDuplicate: false, message: "" };
    try {
      const query = new Parse.Query(Parse.Object.extend("silence1"));
      query.equalTo("playerName", nickname.trim());
      const count = await query.count();
      return count > 0
        ? { isDuplicate: true, message: "이미 사용 중인 닉네임입니다." }
        : { isDuplicate: false, message: "사용 가능한 닉네임입니다." };
    } catch (err) {
      return { isDuplicate: false, message: "" };
    }
  };

  /* ── 힌트 ── */
  const [hintView, setHintView] = useState("NONE");
  const [hintIndex, setHintIndex] = useState(null);
  const [backgroundNone, setBackgroundNone] = useState(false);

  const openHint = () => {
    setHintIndex(null);
    setHintView("LIST");
    setBackgroundNone(true);
  };
  const openHintDetail = (idx) => {
    setHintIndex(idx);
    setHintView("DETAIL");
    setBackgroundNone(true);
  };
  const backFromHint = () => {
    if (hintView === "DETAIL") {
      setHintIndex(null);
      setHintView("LIST");
    } else {
      setHintIndex(null);
      setHintView("NONE");
      setBackgroundNone(false);
    }
  };
  const closeHint = () => {
    setHintIndex(null);
    setHintView("NONE");
    setBackgroundNone(false);
  };

  return (
    <GameContext.Provider
      value={{
        playerName,
        setPlayerName,
        playerKeyCode,
        setPlayerKeyCode,

        introTeamName,
        setIntroTeamName,
        introInCodeV,
        setIntroInCodeV,
        // Intro 컴포넌트 별칭
        introTeam: introTeamName,
        setIntroTeam: setIntroTeamName,
        introInCode: introInCodeV,
        setIntroInCode: setIntroInCodeV,

        // ✅ 관리자 설정 원래값 (클리어시간 계산 기준)
        TOTAL_GAME_TIME: adminGameTime,
        time,
        setTime,
        startTimer: () => setIsRunning(true),
        pauseTimer: () => setIsRunning(false),
        // ✅ LoginName에서 호출 — time만 변경, adminGameTime은 유지
        syncTimer: (t) => {
          setTime(t);
          localStorage.setItem(TIME_STORAGE_KEY, String(t));
        },
        fetchAdminTime,

        MISSION_TOTAL,
        clearedStages,
        setClearedStages,
        increaseMissionOnce,

        hintView,
        hintIndex,
        openHint,
        openHintDetail,
        backFromHint,
        closeHint,
        backgroundNone,

        isFinalQuiz,
        correctCount,
        setCorrectCount,
        checkNicknameDuplicate,
      }}
    >
      {children}
    </GameContext.Provider>
  );
}

export const useGame = () => useContext(GameContext);
